<template>
  <section class="card mt-3" aria-label="Account security" :aria-busy="loading">
    <div class="header border-bottom d-flex justify-content-between align-items-center">
      <h5 class="text-primary font-weight-normal mb-0">Account security</h5>
      <button type="button" class="btn btn-sm btn-outline-primary" @click="load" :disabled="loading">
        {{ loading ? 'Loading…' : 'Refresh' }}
      </button>
    </div>
    <div class="body">
      <p v-if="loading" class="text-muted mb-0" role="status">Loading security status…</p>
      <template v-else>
        <div class="mb-3"><mfa-status-badge :summary="summary" /></div>
        <p v-if="!available" class="text-muted" role="status">Security history is unavailable. Refresh to try again; this does not mean MFA is disabled.</p>
        <div v-else class="row customer-detail">
          <div v-for="(column, index) in checklistColumns" :key="index" class="col-md-6 col-12">
            <ul class="list-unstyled mb-0 striped-list">
              <li v-for="item in column" :key="item.label" class="p-1">
                <div class="row">
                  <div class="col-md-5"><strong>{{ item.label }}:</strong></div>
                  <div class="col-md-7"><span>{{ item.value }}</span></div>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <div v-if="available && summary.devices && summary.devices.length" class="mt-3">
          <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
            <h6 class="mb-2">Devices &amp; verification history</h6>
            <span class="text-muted small mb-2">{{ summary.devices.length }} recorded · {{ summary.trustedDeviceCount }} trusted · {{ summary.activeVerifiedDeviceCount }} trusted with login MFA</span>
          </div>
          <div class="table-responsive">
            <table class="table table-sm security-devices mb-0">
              <thead><tr><th scope="col">Device</th><th scope="col">Current trust</th><th scope="col">Verification evidence</th><th scope="col">Last recorded activity</th><th scope="col">History</th></tr></thead>
              <tbody v-for="(device, index) in summary.devices" :key="index"><tr>
                <td><strong>{{ device.label }}</strong><div v-if="device.model && device.model !== device.label" class="text-muted small">{{ device.model }}</div><div class="text-muted small">{{ device.platform || 'Platform unknown' }} · {{ device.maskedId }}</div></td>
                <td><span :class="device.active ? 'text-success' : 'text-muted'">{{ device.active ? 'Trusted' : device.revokedAt ? 'Revoked' : device.historical ? 'Historical record' : 'Not trusted' }}</span></td>
                <td>
                  <span :title="device.verificationSource === 'login_mfa' ? 'A completed login MFA challenge matches this device and customer. Current trust is shown separately.' : 'Existing records do not reliably distinguish signup/contact OTP, support-granted trust, or other login flows.'" tabindex="0" v-b-tooltip.hover.focus>
                    {{ device.verificationSource === 'login_mfa' ? 'Login MFA completed' : 'Source unconfirmed' }}
                  </span>
                  <div v-if="device.lastLoginVerifiedAt" class="text-muted small">{{ formatDate(device.lastLoginVerifiedAt) }}</div>
                </td>
                <td><span title="Latest activity stored on the device record. This may be a trust update and does not prove a successful login or an active session." tabindex="0" v-b-tooltip.hover.focus>{{ device.lastUsedAt ? formatDate(device.lastUsedAt) : 'Not recorded' }}</span></td>
                <td>
                  <button v-if="device.events && device.events.length" type="button" class="history-toggle" :aria-expanded="!!expanded[index]" :aria-controls="`device-history-${_uid}-${index}`" @click="$set(expanded, index, !expanded[index])">{{ expanded[index] ? 'Hide history' : 'View history' }}</button>
                  <span v-else class="text-muted">Not recorded</span>
                </td>
              </tr>
                <tr v-if="expanded[index]" :id="`device-history-${_uid}-${index}`" class="device-history">
                  <td colspan="5">
                    <div class="small mb-2"><strong>{{ device.label }}</strong><span v-if="device.osVersion"> · {{ device.platform }} {{ device.osVersion }}</span><span v-if="device.appVersion"> · App {{ device.appVersion }}</span></div>
                    <ul class="device-events list-unstyled mb-0">
                      <li v-for="(event, eventIndex) in device.events" :key="eventIndex">{{ event.label }}<div class="text-muted small">{{ formatDate(event.at) }}</div></li>
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p v-else-if="available" class="text-muted small mt-3 mb-0">No device records or device-linked login MFA completions were found.</p>
        <p class="text-muted small border-top pt-3 mt-3 mb-0">Trusted devices are not necessarily signed in.
          <span class="security-help" tabindex="0" v-b-tooltip.hover.focus title="Recorded devices include current device records and past completed login MFA evidence. History shows retained milestones, not every login or device ever used. Signup/contact OTP can establish trust without login MFA. Contact verification alone cannot identify the verified device.">About this evidence ⓘ</span>
        </p>
      </template>
    </div>
  </section>
</template>
<script>
import { ArchBaseService } from "@/common/api.service";
import { formatInUserZone } from "@/utils/datetime";
import MfaStatusBadge from "./MfaStatusBadge.vue";
export default {
  components: { MfaStatusBadge },
  props: { customerId: { type: String, default: "" } },
  data() { return { summary: null, loading: false, requestNumber: 0, expanded: {} }; },
  computed: {
    checklistColumns() {
      if (!this.available) return [];
      const s = this.summary;
      return [
        [
          { label: 'Mobile contact verified', value: this.verifiedLabel(s.mobileVerified) },
          { label: 'Email contact verified', value: this.verifiedLabel(s.emailVerified) },
          { label: 'Login MFA completed', value: s.lastLoginVerifiedAt ? 'Recorded' : 'Not recorded' },
          { label: 'Last login MFA', value: s.lastLoginVerifiedAt ? this.formatDate(s.lastLoginVerifiedAt) : 'Not recorded' },
        ],
        [
          { label: 'Trusted with login MFA', value: s.activeVerifiedDeviceCount },
          { label: 'Currently trusted devices', value: s.trustedDeviceCount },
          { label: 'Signup OTP evidence', value: 'Not separately identifiable' },
          { label: 'Latest login challenge', value: this.challengeLabel },
          { label: 'Latest challenge created', value: s.latestLoginChallenge ? this.formatDate(s.latestLoginChallenge.createdAt) : 'Not recorded' },
        ],
      ];
    },
    available() { return this.summary && this.summary.status !== 'unavailable'; },
    challengeLabel() {
      const challenge = this.summary && this.summary.latestLoginChallenge;
      if (!challenge) return 'Not recorded';
      return ({ CONSUMED: 'Completed', VERIFIED: 'Verified', PENDING: 'Pending', EXPIRED: 'Expired', CANCELLED: 'Cancelled' })[challenge.status] || 'Unknown';
    },
  },
  watch: { customerId: { immediate: true, handler() { this.load(); } } },
  beforeDestroy() { this.requestNumber++; },
  methods: {
    formatDate(value) { return formatInUserZone(value, 'DD MMM YYYY, HH:mm:ss'); },
    verifiedLabel(value) { return value === true || value === 1 ? 'Verified' : value === false || value === 0 ? 'Not verified' : 'Unknown'; },
    async load() {
      const request = ++this.requestNumber;
      this.summary = null;
      this.expanded = {};
      this.loading = !!this.customerId;
      if (!this.customerId) return;
      try {
        ArchBaseService.init();
        ArchBaseService.setHeader();
        const response = await ArchBaseService.get(`customer/${encodeURIComponent(this.customerId)}/mfa-status`);
        if (request === this.requestNumber) this.summary = response.data.details;
      } catch {
        if (request === this.requestNumber) this.summary = null;
      } finally {
        if (request === this.requestNumber) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.security-devices { font-size: 13px; }
.security-devices th, .security-devices td { padding: 12px 10px; vertical-align: top; }
.security-devices th { color: #344250; white-space: nowrap; }
.security-devices .small, .security-help { font-size: 12px; }
.security-devices .text-muted { color: #667481 !important; }
.security-devices summary, .security-help { color: #146c70; cursor: pointer; }
.history-toggle { border: 0; background: none; color: #146c70; padding: 0; white-space: nowrap; cursor: pointer; }
.history-toggle:focus-visible { outline: 2px solid #146c70; outline-offset: 3px; }
.device-history { background: #f5f8f2; }
.device-events { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px 20px; }
.security-devices td:first-child { max-width: 260px; overflow-wrap: anywhere; }
.security-help { display: inline-block; margin-left: 6px; text-decoration: underline dotted; }
</style>
